import { Fragment, memo, useMemo } from 'react';
import { Alert, Box, Grid, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { SvgIconComponent } from '@mui/icons-material';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import AlignVerticalTopIcon from '@mui/icons-material/AlignVerticalTop';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';

import { Accordion } from '@/modules/common/components/Accordion';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { AlignMode } from '@/modules/workspace/types/align';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';

import { BUTTON_GROUP_SX, BUTTON_SX, GAP_XS, GRID_COLUMNS, INPUT_XS, LABEL_XS } from './layout';
import { SelectionDimensionFields } from './SelectionDimensionFields';
import { getEditMode, getGeometry } from './helpers';
import { createItems } from './items';
import { getTemplatesGeometry } from './templates';
import { SelectionDimensionsProps } from './types';
import { useSelectionArrange } from './useSelectionArrange';
import { useSelectionDimensionUpdates } from './useSelectionDimensionUpdates';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  PANEL_ACCORDION_SX,
  FIELDS_PADDING_BOTTOM,
} from '@/modules/common/constants/propertyPanel';

// Two rows of three, like in Figma: left, centre, right | top, middle, bottom
const ALIGN_BUTTONS: { mode: AlignMode; labelKey: string; Icon: SvgIconComponent }[][] = [
  [
    { mode: AlignMode.LEFT, labelKey: 'left', Icon: AlignHorizontalLeftIcon },
    { mode: AlignMode.CENTER, labelKey: 'center', Icon: AlignHorizontalCenterIcon },
    { mode: AlignMode.RIGHT, labelKey: 'right', Icon: AlignHorizontalRightIcon },
  ],
  [
    { mode: AlignMode.TOP, labelKey: 'top', Icon: AlignVerticalTopIcon },
    { mode: AlignMode.MIDDLE, labelKey: 'middle', Icon: AlignVerticalCenterIcon },
    { mode: AlignMode.BOTTOM, labelKey: 'bottom', Icon: AlignVerticalBottomIcon },
  ],
];

function SelectionDimensionsComponent({
  kind,
  selectedIds,
  selectedAreas,
  selectedObstacles,
  selectedLogicalSpaces,
  selectedTemplates,
  hasSelectedGroups,
}: SelectionDimensionsProps) {
  const { t } = useTranslation();
  const appMode = useAppStore((state) => state.mode);
  const editMode = useMemo(
    () => getEditMode(kind, selectedAreas, selectedObstacles, hasSelectedGroups),
    [kind, selectedAreas, selectedObstacles, hasSelectedGroups],
  );
  // Templates are measured through their areas
  const templateAreas = useFloorplanDesignStore(
    useShallow((state) =>
      areasToFlatCollection(
        state.getAreasByIds(selectedTemplates.flatMap((template) => template.group.areaIds)),
      ),
    ),
  );
  const geometry = useMemo(
    () =>
      kind === 'templates'
        ? getTemplatesGeometry(
            createItems(selectedAreas, selectedObstacles, selectedLogicalSpaces),
            selectedTemplates,
            templateAreas,
          )
        : getGeometry(kind, selectedAreas, selectedObstacles, selectedLogicalSpaces, editMode),
    [
      kind,
      selectedAreas,
      selectedObstacles,
      selectedLogicalSpaces,
      selectedTemplates,
      templateAreas,
      editMode,
    ],
  );
  const { onXChange, onYChange, onLengthChange, onWidthChange, onAngleChange } =
    useSelectionDimensionUpdates({
      appMode,
      kind,
      editMode,
      selectedIds,
    });

  const { canRotate, canFlip, flipDisabledReasonKey, canAlign, onAlign, onTransform } =
    useSelectionArrange({
      appMode,
      kind,
      selectedIds,
    });

  if (kind === 'unsupported') {
    return (
      <Accordion
        persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_DIMENSIONS}
        title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
        unmountOnExit
        sx={PANEL_ACCORDION_SX}
      >
        <StyledTooltip titleKey='interface:properties.dimensions.unsupported_tooltip'>
          <Box pb={FIELDS_PADDING_BOTTOM}>
            <Alert severity='info'>{t('interface:properties.dimensions.unsupported_info')}</Alert>
          </Box>
        </StyledTooltip>
      </Accordion>
    );
  }

  if (!geometry) return null;
  const disabled = appMode !== 'EDITABLE';

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_DIMENSIONS}
      title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Box sx={{ mb: 1.5 }}>
        <SelectionDimensionFields
          kind={kind}
          geometry={geometry}
          selectedIds={selectedIds}
          editMode={editMode}
          disabled={disabled}
          onXChange={onXChange}
          onYChange={onYChange}
          onLengthChange={onLengthChange}
          onWidthChange={onWidthChange}
          onAngleChange={onAngleChange}
          canRotate={canRotate}
          canFlip={canFlip}
          flipDisabledReasonKey={flipDisabledReasonKey}
          onTransform={onTransform}
        />
      </Box>
      <Box pb={FIELDS_PADDING_BOTTOM}>
        <Grid container columns={GRID_COLUMNS} alignItems='center'>
          {ALIGN_BUTTONS.map((group, index) => (
            <Fragment key={group[0]?.mode}>
              {index > 0 && <Grid item xs={GAP_XS} />}
              <Grid item xs={LABEL_XS} />
              <Grid item xs={INPUT_XS} sx={BUTTON_GROUP_SX}>
                {group.map(({ mode, labelKey, Icon }) => (
                  <StyledTooltip
                    key={mode}
                    titleKey={`interface:properties.dimensions.align.${labelKey}`}
                  >
                    {/* span: a disabled button fires no events, the tooltip still needs them */}
                    <Box component='span' sx={{ display: 'flex', flex: 1 }}>
                      <IconButton disabled={!canAlign} onClick={() => onAlign(mode)} sx={BUTTON_SX}>
                        <Icon />
                      </IconButton>
                    </Box>
                  </StyledTooltip>
                ))}
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </Box>
    </Accordion>
  );
}

export const SelectionDimensions = memo(SelectionDimensionsComponent);
